import {CalendarApi} from '@fullcalendar/core';
import moment from 'moment';
import {useCallback, useMemo, useRef, useState} from 'react';
import {toISODateString} from 'webapp-lib/utils';

import timetrackerBackend, {
  TimeEntriesPerDay,
  TimeEntryParams,
  TimeEntryResponse,
} from '@lexian/api/timetracker-api';

import {apiEntryToEventInput} from '../utilities/mapping.util';

export interface UseTimeEntriesPerDayProps {
  timeEntriesPerDay: TimeEntriesPerDay | null;
  ongoingTimeEntry: TimeEntryResponse | undefined;
  loadDay: (day: Date) => Promise<void>;
  createTimeEntry: (params: TimeEntryParams) => Promise<void>;
  updateTimeEntry: (
    timeEntryId: string,
    params: TimeEntryParams
  ) => Promise<void>;
  deleteTimeEntry: (timeEntryId: string) => Promise<void>;
}

export function useTimeEntriesPerDay(
  getCalendarApi: () => CalendarApi
): UseTimeEntriesPerDayProps {
  const [timeEntriesPerDay, setTimeEntriesPerDay] =
    useState<TimeEntriesPerDay | null>(null);
  // tracks the day to reload after a mutation
  const currentDayRef = useRef<Date>(new Date());

  const ongoingTimeEntry = timeEntriesPerDay?.timeEntries?.find(
    (timeEntry) => timeEntry.ongoing
  );

  const loadDay = useCallback(
    async (day: Date) => {
      currentDayRef.current = day;
      const api = getCalendarApi();
      if (!api) {
        return;
      }

      await timetrackerBackend.getTimeEntriesPerDay({
        parameters: {day: toISODateString(moment(day))},
        onSuccess: ({body}) => {
          setTimeEntriesPerDay(body);
          api.removeAllEvents();
          body.timeEntries.forEach((entry) =>
            api.addEvent(apiEntryToEventInput(entry))
          );
        },
      });
    },
    [getCalendarApi]
  );

  const createTimeEntry = useCallback(
    async (params: TimeEntryParams) => {
      await timetrackerBackend.createTimeEntry({
        parameters: {body: params},
        onSuccess: async () => {
          await loadDay(currentDayRef.current);
        },
      });
    },
    [loadDay]
  );

  const updateTimeEntry = useCallback(
    async (timeEntryId: string, params: TimeEntryParams) => {
      await timetrackerBackend.updateTimeEntry({
        parameters: {timeEntryId, body: params},
        onSuccess: async () => {
          await loadDay(currentDayRef.current);
        },
      });
    },
    [loadDay]
  );

  const deleteTimeEntry = useCallback(
    async (timeEntryId: string) => {
      await timetrackerBackend.deleteTimeEntry({
        parameters: {timeEntryId},
        onSuccess: async () => {
          await loadDay(currentDayRef.current);
        },
      });
    },
    [loadDay]
  );

  return useMemo(
    () => ({
      timeEntriesPerDay,
      ongoingTimeEntry,
      loadDay,
      createTimeEntry,
      updateTimeEntry,
      deleteTimeEntry,
    }),
    [
      timeEntriesPerDay,
      ongoingTimeEntry,
      loadDay,
      createTimeEntry,
      updateTimeEntry,
      deleteTimeEntry,
    ]
  );
}
